import { toPrivyWallet } from "@privy-io/cross-app-connect/rainbow-kit";
import { createConnector } from "wagmi";

import { publicEnv } from "@/lib/public-env";
import { isPrivyConfigured } from "@/lib/wallet/config";

import type { Wallet } from "@rainbow-me/rainbowkit";

const CHOICE = "mudwater:glyph-evm";
export const GLYPH_EVM_APP = "cly38x0w10ac945q9yg9sm71i";
export const GLYPH_EVM_STAGING_APP = "clxt9p8e601al6tgmsyhu7j3t";

/** An existing Glyph login never chooses an EVM wallet for the holder. */
export function explicitGlyphWallet(wallet: Wallet): Wallet {
  const choice = `${CHOICE}:${wallet.id}`;
  return {
    ...wallet,
    createConnector: (details) =>
      createConnector((config) => {
        let selected = false;
        let generation = 0;
        const chosen = () => {
          try {
            return selected || localStorage.getItem(choice) === "chosen";
          } catch {
            return selected;
          }
        };
        const remember = (value: boolean) => {
          selected = value;
          try {
            if (value) localStorage.setItem(choice, "chosen");
            else localStorage.removeItem(choice);
          } catch {
            // The explicit choice still works when browser storage is denied.
          }
        };
        // Each attempt owns its emitter. Even an event emitted after an await
        // inside the vendor connector cannot revive a superseded connection.
        const make = (ticket: number) =>
          wallet.createConnector(details)({
            ...config,
            emitter: new Proxy(config.emitter, {
              get(target, property) {
                if (property === "emit")
                  return (...args: unknown[]) => {
                    if (ticket === generation && chosen())
                      Reflect.apply(target.emit, target, args);
                  };
                const value: unknown = Reflect.get(target, property, target);
                return typeof value === "function" ? value.bind(target) : value;
              },
            }),
          });
        let connector = make(generation);
        return {
          ...connector,
          async setup() {
            if (chosen()) await connector.setup?.();
          },
          async isAuthorized() {
            if (!chosen()) return false;
            const ticket = generation;
            const authorized = await connector.isAuthorized();
            return ticket === generation && chosen() && authorized;
          },
          async connect<withCapabilities extends boolean = false>(
            parameters?: Parameters<
              typeof connector.connect<withCapabilities>
            >[0]
          ): ReturnType<typeof connector.connect<withCapabilities>> {
            if (parameters?.isReconnecting && !chosen())
              throw new Error("Choose Glyph in the EVM wallet picker first.");
            const ticket = ++generation;
            remember(true);
            const attempt = make(ticket);
            connector = attempt;
            try {
              await attempt.setup?.();
              if (ticket !== generation || !chosen())
                throw new Error(
                  "Glyph connection was cancelled. Choose a wallet again."
                );
              const result =
                await attempt.connect<withCapabilities>(parameters);
              if (ticket !== generation || !chosen())
                throw new Error(
                  "Glyph connection was cancelled. Choose a wallet again."
                );
              return result;
            } catch (error) {
              if (ticket === generation) remember(false);
              throw error;
            }
          },
          async getProvider(parameters) {
            return connector.getProvider(parameters);
          },
          async getAccounts() {
            if (!chosen()) return [];
            const ticket = generation;
            const accounts = await connector.getAccounts();
            return ticket === generation && chosen() ? accounts : [];
          },
          async getChainId() {
            return connector.getChainId();
          },
          async switchChain(parameters) {
            if (!connector.switchChain)
              throw new Error("Glyph cannot switch networks.");
            return connector.switchChain(parameters);
          },
          onConnect(info) {
            if (chosen()) connector.onConnect?.(info);
          },
          onAccountsChanged(accounts) {
            if (chosen() || accounts.length === 0)
              connector.onAccountsChanged(accounts);
          },
          onChainChanged(chain) {
            if (chosen()) connector.onChainChanged(chain);
          },
          onDisconnect(error) {
            if (chosen()) connector.onDisconnect(error);
          },
          async disconnect() {
            const previous = connector;
            ++generation;
            remember(false);
            await previous.disconnect();
          },
        };
      }),
  };
}

/** Glyph is an EVM choice, separate from account login and Solana delivery. */
export function glyphEvmWallets() {
  const flag = publicEnv("NEXT_PUBLIC_MUDWATER_GLYPH_SIGNIN");
  const mainnet = publicEnv("NEXT_PUBLIC_MUDWATER_EVM_CHAIN_ID") === "1";
  if (!isPrivyConfigured || flag === "off" || (mainnet && flag !== "on"))
    return [];
  const wallet = toPrivyWallet({
    id: mainnet ? GLYPH_EVM_APP : GLYPH_EVM_STAGING_APP,
    name: "Glyph",
    iconUrl: "/wallet-icons/glyph.png",
  });
  return [() => explicitGlyphWallet(wallet())];
}
